import React, { useState, lazy, Suspense } from "react";
import HeroBannerSec from "../sections/homepage/HeroBannerSec";
import MapSec from "../sections/homepage/MapSec";
import { fetchAPI } from "../helper/fetchAPI";
import * as constants from "../constants";
import CustomHelmet from "../components/CustomHelmet";
import FullScreenLoader from "../components/FullScreenLoader";
import LazyOnVisible from "../components/LazyOnVisible";
import { useTranslation } from "react-i18next";
import { useCountryPrefix } from "../routes/useCountryPrefix";
import { usePageLoading } from "../context/PageLoadingContext";
import { initAOS, refreshAOS } from "../helper/initAOS";

const PropertyList = lazy(() => import("../components/PropertyList"));
const NewLaunch = lazy(() => import("../components/NewLaunch"));
const TopAreaSec = lazy(() => import("../sections/homepage/TopAreaSec"));
const OurPartnersSec = lazy(() => import("../sections/homepage/OurPartnersSec"));
const News = lazy(() => import("../components/News"));
const ContactUsForm = lazy(() => import("../components/ContactUsForm"));

const Home = () => {
  let apiUrl = process.env.REACT_APP_API_URL;
  let filterApiUrl = process.env.REACT_APP_API_URL;
  const countryPrefix = useCountryPrefix();
  const { setPageLoading } = usePageLoading();

  const { i18n } = useTranslation();
  const [homeSection, setHomeSection] = useState();
  const [mapSection, setMapSection] = useState();
  const [offerSection, setOfferSection] = useState();
  const [newLaunchSection, setNewLaunchSection] = useState();
  const [topAreaSection, setTopAreaSection] = useState();
  const [propertyListSection, setPropertyListSection] = useState();
  const [propertyTypesSection, setPropertyTypesSection] = useState();
  const [propertiesSection, setPropertiesSection] = useState();
  const [ourPartnersSection, setOurPartnersSection] = useState();
  const [ourNewsSection, setOurNewsSection] = useState();
  const [filterSection, setFilterSection] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [contactDetails, setContactDetails] = useState();

  const fetchData = async () => {
    const isArabic = i18n.language === "ar";
    const language = isArabic ? "ar" : "en";
    try {
      apiUrl = `${apiUrl}${constants.homepage}?language=${language}&region_id=${countryPrefix}`;
      const res = await fetchAPI(apiUrl, "Get");
      if (res?.status === 200) {
        const section = res.data.sections;
        setHomeSection(section.find((item) => item.sectionId === "HeroSection"));
        setMapSection(section.find((item) => item.sectionId === "MapSection"));
        setOfferSection(section.find((item) => item.sectionId === "OfferSection"));
        setNewLaunchSection(section.find((item) => item.sectionId === "LaunchSection"));
        setTopAreaSection(section.find((item) => item.sectionId === "AreaSection"));
        setPropertyListSection(section.find((item) => item.sectionId === "PropertySection"));

        const sectionData = section.find((item) => item.sectionId === "PropertySection");
        setPropertyTypesSection(
          sectionData.sectionItems.find((item) => item.propertyTypes)?.propertyTypes
        );
        setPropertiesSection({
          items: sectionData.sectionItems.find((item) => item.items)?.items,
        });

        setOurPartnersSection(section.find((item) => item.sectionId === "PartnerSection"));
        setOurNewsSection(section.find((item) => item.sectionId === "NewsSection"));

        setContactDetails(res.data.contactDetails);
        setIsLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setPageLoading(false);
    }

    try {
      filterApiUrl = `${filterApiUrl}${constants.searchfilters}?language=${language}&region_id=${countryPrefix}`;
      const resFilter = await fetchAPI(filterApiUrl, "Get");
      if (resFilter?.status === 200) {
        setFilterSection(resFilter?.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  React.useEffect(() => {
    setPageLoading(true);
    fetchData();
    return () => setPageLoading(false);
  }, []);

  React.useEffect(() => {
    if (isLoaded) {
      setPageLoading(false);
      initAOS();
      refreshAOS();
    }
  }, [isLoaded, setPageLoading]);

  if (!isLoaded) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <CustomHelmet
        title={"Exclusive Real Estate Across Egypt"}
        desc={"Connect Homes makes exploring real estate in Egypt simple and seamless."}
      />
      <HeroBannerSec data={homeSection || {}} filters={filterSection} />
      <MapSec data={mapSection || {}} offerData={offerSection || {}} />

      <LazyOnVisible minHeight={480}>
        <Suspense fallback={null}>
          <PropertyList
            data={propertyListSection}
            propertyTypeData={propertyTypesSection}
            propertiesData={propertiesSection}
            isUrlAppend={true}
            contact={contactDetails}
          />
        </Suspense>
      </LazyOnVisible>

      <LazyOnVisible minHeight={280}>
        <Suspense fallback={null}>
          <NewLaunch data={newLaunchSection || {}} />
        </Suspense>
      </LazyOnVisible>

      <LazyOnVisible minHeight={360}>
        <Suspense fallback={null}>
          <TopAreaSec data={topAreaSection || {}} />
        </Suspense>
      </LazyOnVisible>

      <LazyOnVisible minHeight={280}>
        <Suspense fallback={null}>
          <OurPartnersSec data={ourPartnersSection || {}} />
        </Suspense>
      </LazyOnVisible>

      <LazyOnVisible minHeight={360}>
        <Suspense fallback={null}>
          <News data={ourNewsSection || {}} />
        </Suspense>
      </LazyOnVisible>

      <LazyOnVisible minHeight={400}>
        <Suspense fallback={null}>
          <ContactUsForm imgDiv={false} />
        </Suspense>
      </LazyOnVisible>
    </>
  );
};

export default Home;
